import { IGPKeyInitiative } from 'legacy/lut/classes/models/partner360/IGPKeyInitiative'
import { getBusinessBridgeCrud } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/commercialBridges/cruds/getBusinessBridgeCrud'
import { TRANSLATION_KEYS } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/translations'
import { useJbpSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { FC, useCallback, useMemo, useState } from 'react'

import MeasureController from 'core/analytics/MeasureController'
import { filterKeyInitiativeSettingsByYear } from 'partner360/features/keyInitiativeEditor/lib/filterKeyInitiativeSettingsByYear'
import { getKeyInitiativeProgramType } from 'partner360/features/keyInitiativeEditor/lib/getKeyInitiativeProgramType'

import { getJbpBridgesCrud } from '../../../../../../commercialBridges/components/businessBridges/helpers/getJbpBridgesCrud'
import { applyKeyInitiativeChanges } from '../../../../../api/applyKeyInitiativeChanges'
import { useKeyInitiativesEditor } from '../../../../../hooks/useKeyInitiativesEditor'
import { IApplyKeyInitiativeOptions, IGPFormKIEditor } from '../../../../../models'
import { checkAffectedLP } from '../../api/checkAffectedLP'
import { AffectedLPModal } from '../affectedLpModal/AffectedLPModal'

import { GPKeyInitiativeEditorModal } from './components/gpKeyInitiativeEditorModal/GPKeyInitiativeEditorModal'
import { mapFormToGPKeyInitiative } from './helpers/mapFormToGPKeyInitiative'
import { mapGPKeyInitiativeToForm } from './helpers/mapGPKeyInitiativeToForm'
import { IGPKeyInitiativeForm } from './models/gpKeyInitiativeForm'

interface IKeyInitiativeEditorProps {
  initialState?: Partial<IGPFormKIEditor>
  isDialogOpened: boolean
  isDataInsightPresent: boolean
  metrics?: MeasureController
  isDataInsightTab?: boolean
  keyObjectivesFilter?: string

  onLoadingStateChange: (isLoading: boolean) => void
  onEditCancel: () => void
  onUpdateFinished: (newData: IGPKeyInitiative[]) => void
}

export const GPKeyInitiativesEditor: FC<IKeyInitiativeEditorProps> = ({
  initialState,
  isDialogOpened,
  isDataInsightPresent,
  isDataInsightTab,

  onLoadingStateChange,
  onEditCancel,
  onUpdateFinished,
}) => {
  const [isShowAffectedLPModal, setIsShowAffectedLPModal] = useState(false)
  const [affectedLPCount, setAffectedLPCount] = useState(0)
  const [payloadForSave, setPayloadForSave] =
    useState<IApplyKeyInitiativeOptions<IGPKeyInitiative> | null>(null)
  const [shouldAddToBridge, setShouldAddToBridge] = useState(false)

  const { isCommercial, recalculateCompleteness, ownerId, fYear, programType, partnerId } =
    useKeyInitiativesEditor({ entryId: initialState?.EntryId })
  const { jbpSettings } = useJbpSettings()
  const keyObjectivesForYear = useMemo(
    () => filterKeyInitiativeSettingsByYear(jbpSettings.keyObjectives, fYear),
    [fYear, jbpSettings.keyObjectives]
  )

  const mappedInitialState = useMemo(() => {
    return mapGPKeyInitiativeToForm(initialState ?? {})
  }, [initialState])

  const openAffectedLPModal = useCallback(() => {
    setIsShowAffectedLPModal(true)
  }, [])

  const closeAffectedLPModal = useCallback(() => {
    setIsShowAffectedLPModal(false)
  }, [])

  const applyChanges = useCallback(
    async (payload: IApplyKeyInitiativeOptions<IGPKeyInitiative>, shouldAddToBridges: boolean) => {
      onLoadingStateChange(true)

      const data = await applyKeyInitiativeChanges<IGPKeyInitiative>(payload)

      if (payload.action === 'Create' && data?.length === 2) {
        recalculateCompleteness()
      }
      if (shouldAddToBridges || mappedInitialState?.shouldAddToBridges) {
        getBusinessBridgeCrud().removeQueries(true)
        getJbpBridgesCrud().removeQueries(true)
      }
      onUpdateFinished(data ?? [])
      onLoadingStateChange(false)
    },
    [
      mappedInitialState?.shouldAddToBridges,
      onLoadingStateChange,
      onUpdateFinished,
      recalculateCompleteness,
    ]
  )

  const onAgree = useCallback(
    (payload: IApplyKeyInitiativeOptions<IGPKeyInitiative> | null, shouldAddToBridges: boolean) =>
      async () => {
        if (payload) {
          await applyChanges(payload, shouldAddToBridges)
        }
        closeAffectedLPModal()
      },
    [applyChanges, closeAffectedLPModal]
  )

  const handleApply = useCallback(
    async (
      form: IGPKeyInitiativeForm,
      isInvestmentNeeds: boolean,
      needCheckAffectedLp?: boolean
    ) => {
      const mappedData = mapFormToGPKeyInitiative(form, isInvestmentNeeds)
      const action = mappedData.EntryId ? 'Update' : 'Create'
      const payload: IApplyKeyInitiativeOptions<IGPKeyInitiative> = {
        ownerId,
        fYear,
        programType: getKeyInitiativeProgramType(
          form.keyObjective,
          keyObjectivesForYear,
          programType
        ),
        partnerId,
        data: mappedData,
        action,
      }
      setPayloadForSave(payload)
      setShouldAddToBridge(form.shouldAddToBridges)
      if (needCheckAffectedLp) {
        const affectedLp = await checkAffectedLP(payload)
        if (affectedLp?.length) {
          setAffectedLPCount(affectedLp.length)
          openAffectedLPModal()
          return
        }
      }

      await applyChanges(payload, form.shouldAddToBridges)
      getJbpBridgesCrud().removeQueries(true)
    },
    [
      applyChanges,
      fYear,
      keyObjectivesForYear,
      openAffectedLPModal,
      ownerId,
      partnerId,
      programType,
    ]
  )

  return (
    <>
      <GPKeyInitiativeEditorModal
        initialData={mappedInitialState}
        isOpen={isDialogOpened}
        isDataInsightPresent={isDataInsightPresent}
        isDataInsightTab={isDataInsightTab}
        programType={programType}
        onApply={handleApply}
        onCancel={onEditCancel}
        isCommercial={isCommercial}
      />

      <AffectedLPModal
        show={isShowAffectedLPModal}
        affectedLPCount={affectedLPCount}
        onCancel={closeAffectedLPModal}
        onAgree={onAgree(payloadForSave, shouldAddToBridge)}
        headerTranslation={TRANSLATION_KEYS.affectedLPModal.headerUpdate}
        textTranslation={TRANSLATION_KEYS.affectedLPModal.textUpdate}
      />
    </>
  )
}
